import { z } from "zod";
import type { Path, UseFormReturn } from "react-hook-form";
import { EMAIL_RE, digitsLen } from "@/utils/validation";
import { req, opt, numAmount, digitsReq, digitsOpt, digitsRangeReq, zipField, lettersReq, lettersOpt, isSoleProprietor } from "@/features/onboarding/components/wizard";
import { FIELD_MAX, tooLong } from "@/lib/fieldLimits";

export const STEPS = ["Overview", "Legal", "Principal", "Bank"] as const;

export const schema = z.object({
  legalName: req("Corporate / legal name is required.").max(FIELD_MAX.appWizard.legalName, tooLong("Corporate / legal name", FIELD_MAX.appWizard.legalName)),
  dbaSameAsLegal: z.boolean(),
  dbaName: opt().max(FIELD_MAX.appWizard.dbaName, tooLong("DBA name", FIELD_MAX.appWizard.dbaName)),
  website: opt().max(FIELD_MAX.appWizard.website, tooLong("Website", FIELD_MAX.appWizard.website)),
  ownershipType: req("Choose an ownership type.").max(FIELD_MAX.appWizard.ownershipType, tooLong("Ownership type", FIELD_MAX.appWizard.ownershipType)),
  businessStartDate: opt().max(FIELD_MAX.appWizard.businessStartDate, tooLong("Date business started", FIELD_MAX.appWizard.businessStartDate)),
  mcc: digitsOpt(4, FIELD_MAX.appWizard.mcc, "MCC"),
  addressLine1: req("Business address is required.").max(FIELD_MAX.appWizard.addressLine1, tooLong("Business address", FIELD_MAX.appWizard.addressLine1)),
  addressLine2: opt().max(FIELD_MAX.appWizard.addressLine2, tooLong("Address line 2", FIELD_MAX.appWizard.addressLine2)),
  city: req("City is required.").max(FIELD_MAX.appWizard.city, tooLong("City", FIELD_MAX.appWizard.city)),
  state: req("State is required.").max(FIELD_MAX.appWizard.state, tooLong("State", FIELD_MAX.appWizard.state)),
  zip: zipField(true, FIELD_MAX.appWizard.zip, "ZIP"),
  dbaAddressLine1: opt().max(FIELD_MAX.appWizard.dbaAddressLine1, tooLong("DBA street address", FIELD_MAX.appWizard.dbaAddressLine1)),
  dbaCity: opt().max(FIELD_MAX.appWizard.dbaCity, tooLong("DBA city", FIELD_MAX.appWizard.dbaCity)),
  dbaState: opt().max(FIELD_MAX.appWizard.dbaState, tooLong("DBA state", FIELD_MAX.appWizard.dbaState)),
  dbaZip: zipField(false, FIELD_MAX.appWizard.dbaZip, "DBA ZIP"),
  contactName: opt().max(FIELD_MAX.appWizard.contactName, tooLong("Contact name", FIELD_MAX.appWizard.contactName)),
  email: z.string().trim().min(1, "An email address is required.").regex(EMAIL_RE, "Enter a valid email address.").max(FIELD_MAX.appWizard.email, tooLong("Email", FIELD_MAX.appWizard.email)),
  phone: digitsReq(10, FIELD_MAX.appWizard.phone, "Business phone"),
  averageMonthlyVolume: numAmount(),
  averageTransactionAmount: numAmount(),
  maxTransactionAmount: numAmount(),
  productDescription: opt().max(FIELD_MAX.appWizard.productDescription, tooLong("Product description", FIELD_MAX.appWizard.productDescription)),
  ownerCitizenship: z.enum(["us", "non-us"]),
  ownerFirstName: lettersReq(FIELD_MAX.appWizard.ownerFirstName, "First name"),
  ownerMiddleName: lettersOpt(FIELD_MAX.appWizard.ownerMiddleName, "Middle name"),
  ownerLastName: lettersReq(FIELD_MAX.appWizard.ownerLastName, "Last name"),
  ownerDateOfBirth: req("Date of birth is required.").max(FIELD_MAX.appWizard.ownerDateOfBirth, tooLong("Date of birth", FIELD_MAX.appWizard.ownerDateOfBirth)),
  ownerSsn: digitsReq(9, FIELD_MAX.appWizard.ownerSsn, "SSN"),
  ownerPhone: digitsReq(10, FIELD_MAX.appWizard.ownerPhone, "Phone number"),
  ownerHomeAddressLine1: req("Street address is required.").max(FIELD_MAX.appWizard.ownerHomeAddressLine1, tooLong("Street address", FIELD_MAX.appWizard.ownerHomeAddressLine1)),
  ownerHomeCity: req("City is required.").max(FIELD_MAX.appWizard.ownerHomeCity, tooLong("City", FIELD_MAX.appWizard.ownerHomeCity)),
  ownerHomeState: req("State is required.").max(FIELD_MAX.appWizard.ownerHomeState, tooLong("State", FIELD_MAX.appWizard.ownerHomeState)),
  ownerHomeZip: zipField(true, FIELD_MAX.appWizard.ownerHomeZip, "ZIP"),
  ein: digitsOpt(9, FIELD_MAX.appWizard.ein, "EIN"),
  ssn: digitsOpt(9, FIELD_MAX.appWizard.ssn, "SSN"),
  bankName: opt().max(FIELD_MAX.appWizard.bankName, tooLong("Bank name", FIELD_MAX.appWizard.bankName)),
  depositAccountType: opt().max(FIELD_MAX.appWizard.depositAccountType, tooLong("Account type", FIELD_MAX.appWizard.depositAccountType)),
  bankRoutingNumber: digitsReq(9, FIELD_MAX.appWizard.bankRoutingNumber, "Routing number"),
  bankAccountNumber: digitsRangeReq(1, 17, FIELD_MAX.appWizard.bankAccountNumber, "Account number"),
  separateWithdrawAccount: z.boolean(),
  withdrawBankName: opt().max(FIELD_MAX.appWizard.withdrawBankName, tooLong("Bank name", FIELD_MAX.appWizard.withdrawBankName)),
  withdrawAccountType: opt().max(FIELD_MAX.appWizard.withdrawAccountType, tooLong("Account type", FIELD_MAX.appWizard.withdrawAccountType)),
  withdrawRoutingNumber: opt(),
  withdrawAccountNumber: opt(),
}).superRefine((v, ctx) => {
  if (v.separateWithdrawAccount) {
    if (!v.withdrawRoutingNumber.trim()) {
      ctx.addIssue({ code: "custom", path: ["withdrawRoutingNumber"], message: "Withdraw routing number is required." });
    } else if (!digitsLen(v.withdrawRoutingNumber, 9)) {
      ctx.addIssue({ code: "custom", path: ["withdrawRoutingNumber"], message: "Withdraw routing number must be 9 digits." });
    }
    const accountDigits = v.withdrawAccountNumber.replace(/\D/g, "").length;
    if (!v.withdrawAccountNumber.trim()) {
      ctx.addIssue({ code: "custom", path: ["withdrawAccountNumber"], message: "Withdraw account number is required." });
    } else if (accountDigits < 1 || accountDigits > 17) {
      ctx.addIssue({ code: "custom", path: ["withdrawAccountNumber"], message: "Withdraw account number must be 1–17 digits." });
    }
  }
  if (isSoleProprietor(v.ownershipType)) {
    if (!v.ssn.trim()) ctx.addIssue({ code: "custom", path: ["ssn"], message: "An SSN is required for a sole proprietor." });
  } else if (!v.ein.trim()) {
    ctx.addIssue({ code: "custom", path: ["ein"], message: "An EIN is required." });
  }
  if (!v.dbaSameAsLegal && !v.dbaName.trim()) {
    ctx.addIssue({ code: "custom", path: ["dbaName"], message: "Enter the DBA name, or choose “Yes, use legal name”." });
  }
});

export type FormState = z.infer<typeof schema>;

export interface StepProps {
  form: UseFormReturn<FormState>;
}

export const STEP_FIELDS: Partial<Record<number, Path<FormState>[]>> = {
  1: ["legalName", "email", "phone", "addressLine1", "zip", "state", "city", "ownershipType", "ssn", "ein", "dbaName"],
  2: ["ownerFirstName", "ownerMiddleName", "ownerLastName", "ownerDateOfBirth", "ownerSsn", "ownerPhone", "ownerHomeAddressLine1", "ownerHomeZip", "ownerHomeState", "ownerHomeCity"],
  3: ["bankRoutingNumber", "bankAccountNumber", "separateWithdrawAccount", "withdrawBankName", "withdrawAccountType", "withdrawRoutingNumber", "withdrawAccountNumber"],
};

export const EMPTY_FORM: FormState = {
  legalName: "", dbaSameAsLegal: true, dbaName: "", website: "",
  ownershipType: "", businessStartDate: "", mcc: "",
  addressLine1: "", addressLine2: "", city: "", state: "", zip: "",
  dbaAddressLine1: "", dbaCity: "", dbaState: "", dbaZip: "",
  contactName: "", email: "", phone: "",
  averageMonthlyVolume: "", averageTransactionAmount: "", maxTransactionAmount: "",
  productDescription: "",
  ownerCitizenship: "us",
  ownerFirstName: "", ownerMiddleName: "", ownerLastName: "", ownerDateOfBirth: "",
  ownerSsn: "", ownerPhone: "",
  ownerHomeAddressLine1: "", ownerHomeCity: "", ownerHomeState: "", ownerHomeZip: "",
  ein: "", ssn: "", bankName: "", depositAccountType: "",
  bankRoutingNumber: "", bankAccountNumber: "",
  separateWithdrawAccount: false, withdrawBankName: "", withdrawAccountType: "",
  withdrawRoutingNumber: "", withdrawAccountNumber: "",
};

export function documentName(file: File | null, base: string): string | null {
  if (!file) return null;
  const safe = base.trim().replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "document";
  const dot = file.name.lastIndexOf(".");
  const ext = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : "jpg";
  return `${safe}.${ext}`;
}
